import { errorToast, successToast } from '@hooks/toast.hook';
import { useAdminDeleteOrganization, useAdminRestoreOrganization } from '@queries/admin-organizations.query';
import { useCallback } from 'react';

type UseOrganizationActionsParams = {
  onDeleteSuccess?: () => void;
  onRestoreSuccess?: () => void;
  organizationId: string;
};

type UseOrganizationActionsReturn = {
  handleDelete: () => Promise<void>;
  handleRestore: () => Promise<void>;
  isBusy: boolean;
  isDeleting: boolean;
  isRestoring: boolean;
};

/**
 * Hook for managing organization delete/restore actions.
 * Centralizes the logic to avoid duplication between modal content and footer.
 */
export const useOrganizationActions = (params: UseOrganizationActionsParams): UseOrganizationActionsReturn => {
  const { onDeleteSuccess, onRestoreSuccess, organizationId } = params;

  const { isPending: isDeleting, mutateAsync: deleteOrganization } = useAdminDeleteOrganization();
  const { isPending: isRestoring, mutateAsync: restoreOrganization } = useAdminRestoreOrganization();

  const isBusy = isDeleting || isRestoring;

  const handleDelete = useCallback(async () => {
    await deleteOrganization(organizationId, {
      onError: (error) => errorToast({ description: error.message || 'Failed to delete organization.' }),
      onSuccess: () => {
        successToast({ description: 'Organization deleted successfully.' });
        onDeleteSuccess?.();
      },
    });
  }, [deleteOrganization, onDeleteSuccess, organizationId]);

  const handleRestore = useCallback(async () => {
    try {
      await restoreOrganization(organizationId);
      successToast({ description: 'Organization restored successfully.' });
      onRestoreSuccess?.();
    } catch (error) {
      console.error('Failed to restore organization', error);
      errorToast({ description: 'Failed to restore organization.' });
    }
  }, [onRestoreSuccess, organizationId, restoreOrganization]);

  return {
    handleDelete,
    handleRestore,
    isBusy,
    isDeleting,
    isRestoring,
  };
};
